import React, { useEffect, useState } from "react";
import { Grid, TextField, Autocomplete, Chip } from "@mui/material";
import { Button } from "antd";
import AdvancedTable from "../../sharedComponents/AdvancedTable/AdvancedTable";
import { getProjectWorkingReport, downloadProjectWorkingReport } from "../../services/reports.service";
import { getUser } from "../../services/user.service";
import { getAllProject } from "../../services/reports.service";
import { DatePicker } from "antd";
import dayjs from "dayjs";
import { ISSUETYPE } from "../../common/constant/constant";
import ClientTaskDetailsDialog from "./Components/ClientTaskDetailsDialog";

const { RangePicker } = DatePicker;

const ProjectReportTable = () => {
  const [users, setUsers] = useState([]);
  const [selectedUser, setSelectedUser] = useState(null);
  const [projects, setProjects] = useState([]);
  const [selectedProjects, setSelectedProjects] = useState([]);
  const [issueType, setIssueType] = useState(null);
  const [loading, setLoading] = useState(true);
  const [downloadLoading, setDownloadLoading] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogData, setDialogData] = useState({
    projectId: null,
    projectName: "",
    date: "",
    userId: null,
    issueType: null,
  });

  const initialMonth = dayjs();
  const [dateRange, setDateRange] = useState({
    startDate: initialMonth.startOf("month"),
    endDate: initialMonth,
  });
  const [data, setData] = useState([]);
  const [dateColumns, setDateColumns] = useState([]);
  const issueTypeOptions = [
    { label: "All", value: null },
    { label: "Bug", value: ISSUETYPE.BUG },
    { label: "Task", value: ISSUETYPE.TASK },
    { label: "Meeting", value: ISSUETYPE.MEETING },
    { label: "Testing", value: ISSUETYPE.TESTING },
    { label: "Designing", value: ISSUETYPE.DESIGNING },
    { label: "Others", value: ISSUETYPE.OTHERS },
    { label: "Deployment", value: ISSUETYPE.DEPLOYMENT },
  ];

  const [payableFilter, setPayableFilter] = useState("All");
  const payableOptions = [
    { label: "All", value: "All" },
    { label: "Payable", value: "true" },
    { label: "Unpayable", value: "false" },
  ];

  // Fetch all users
  useEffect(() => {
    getUser({ isEnabled: true }).then((res) => {
      const userList = Array.isArray(res.data?.data) ? res.data.data : [];
      setUsers([{ _id: "All", name: "All" }, ...userList]);
    });
  }, []);

  // Fetch projects
  useEffect(() => {
    getAllProject({ isEnabled: true }).then((res) => {
      const projectList = Array.isArray(res.data) ? res.data : [];
      setProjects(projectList);
    });
  }, []);

  // Fetch report whenever filters change
  useEffect(() => {
    const fetchReport = async () => {
      setLoading(true);
      try {
        const projectIds = selectedProjects.length > 0
          ? selectedProjects.map(p => p._id).join(',')
          : null;

        const res = await getProjectWorkingReport({
          startDate: dateRange.startDate
            ? dateRange.startDate.startOf("day").toISOString()
            : null,
          endDate: dateRange.endDate
            ? dateRange.endDate.endOf("day").toISOString()
            : null,
          userId: selectedUser?._id || "All",
          issueType: issueType,
          projectIds: projectIds,
          payable: payableFilter === "All" ? null : payableFilter,
        });
        const reportData = Array.isArray(res.data) ? res.data : [];
        setData(reportData);
      } catch (error) {
        console.error("Error fetching project report:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchReport();
  }, [selectedUser, dateRange, issueType, selectedProjects, payableFilter]);

  // Collect unique dates from data
  useEffect(() => {
    const days = new Set();
    data.forEach((project) => {
      project.dates.forEach((d) => days.add(d.day));
    });
    setDateColumns([...days].sort());
  }, [data]);

  const handleCellClick = (row, column) => {
    if (column.isClickable && row[column.id] !== "0.00") {
      setDialogData({
        projectId: row.projectId,
        projectName: row.projectName,
        date: column.id,
        userId: selectedUser?._id || "All",
        issueType: issueType,
      });
      setDialogOpen(true);
    }
  };

  const handleDialogClose = () => {
    setDialogOpen(false);
    setDialogData({ projectId: null, projectName: "", date: "", userId: null, issueType: null });
  };

  // Prepare table columns
  const columns = [
    { id: "projectName", label: "Project Name" },
    ...dateColumns.map((day) => {
      const dayLabel = dayjs(day).format("MMM-DD");
      return { id: day, label: dayLabel, isTotal: true, totalType: "time", isClickable: true };
    }),
    { id: "Total", label: "Total Hours", isTotal: true, isTotalColumn: true, totalType: "time" },
  ];
  const getAllDatesInRange = (start, end) => {
    const dates = [];
    let current = start.startOf("day");
    const last = end.startOf("day");
    while (current.isBefore(last) || current.isSame(last)) {
      dates.push(current.format("YYYY-MM-DD"));
      current = current.add(1, "day");
    }
    return dates;
  };

  // In your useEffect for columns
  useEffect(() => {
    if (dateRange.startDate && dateRange.endDate) {
      setDateColumns(getAllDatesInRange(dateRange.startDate, dateRange.endDate));
    } else {
      setDateColumns([]);
    }
  }, [dateRange]);

  const transformedData = data.map((project) => {
    const row = {
      projectName: project.projectName,
      projectId: project.projectId || project._id // Ensure projectId is available
    };
    let totalMinutes = 0;

    dateColumns.forEach((day) => {
      const entry = project.dates.find((d) => d.day === day);
      if (entry) {
        const hours = Math.floor(entry.totalHours);
        const mins = Math.round((entry.totalHours % 1) * 100);
        totalMinutes += hours * 60 + mins;

        row[day] = `${hours}.${mins.toString().padStart(2, "0")}`;
      } else {
        row[day] = "0.00";
      }
    });

    const totalHours = Math.floor(totalMinutes / 60);
    const totalMins = totalMinutes % 60;
    row.Total = `${totalHours}.${totalMins.toString().padStart(2, "0")}`;

    return row;
  });
  transformedData.sort((a, b) => a.projectName.localeCompare(b.projectName));

  const handleDownload = async () => {
    setDownloadLoading(true);
    try {
      const projectIds = selectedProjects.length > 0
        ? selectedProjects.map(p => p._id).join(',')
        : null;

      const response = await downloadProjectWorkingReport({
        startDate: dateRange.startDate
          ? dateRange.startDate.startOf("day").toISOString()
          : null,
        endDate: dateRange.endDate
          ? dateRange.endDate.endOf("day").toISOString()
          : null,
        userId: selectedUser?._id || "All",
        issueType: issueType !== undefined && issueType !== null ? issueType : "All",
        projectIds: projectIds,
      });

      // Handle blob response
      const blob = new Blob([response], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
      });

      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;

      const startStr = dateRange.startDate.format("YYYY-MM-DD");
      const endStr = dateRange.endDate.format("YYYY-MM-DD");
      link.setAttribute("download", `Project_Working_Report_${startStr}_to_${endStr}.xlsx`);

      document.body.appendChild(link);
      link.click();

      setTimeout(() => {
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
      }, 100);
    } catch (error) {
      console.error("Error downloading report:", error);
      alert("Failed to download report");
    } finally {
      setDownloadLoading(false);
    }
  };

  const tableConfig = {
    isAction: false,
    isEdit: false,
    isMore: false,
    isAdmin: false,
  };
  return (
    <div className="app-container">
      <Grid container spacing={2} mb={2}>
        <Grid item>
          <RangePicker
            value={[
              dateRange.startDate ? dayjs(dateRange.startDate) : null,
              dateRange.endDate ? dayjs(dateRange.endDate) : null,
            ]}
            onChange={(dates) => {
              const [start, end] = dates || [];
              if (start && end) {
                setDateRange({
                  startDate: dayjs(start).startOf("day"),
                  endDate: dayjs(end).endOf("day"),
                });
              } else {
                const initialMonth = dayjs();
                setDateRange({
                  startDate: initialMonth.startOf("month"),
                  endDate: initialMonth.endOf("month"),
                });
              }
            }}
            className="datePicker w-100"
            format={"DD-MM-YYYY"}
          />
        </Grid>
        <Grid item>
          <Autocomplete
            options={users}
            getOptionLabel={(u) => u.name || ""}
            value={selectedUser}
            onChange={(e, val) => setSelectedUser(val)}
            renderInput={(params) => (
              <TextField {...params} label="Select User" />
            )}
            sx={{ width: 200 }}
          />
        </Grid>
        <Grid item>
          <Autocomplete
            value={
              issueTypeOptions.find((opt) => opt.value === issueType) ||
              issueTypeOptions[0]
            }
            options={issueTypeOptions}
            onChange={(e, v) => setIssueType(v?.value)}
            getOptionLabel={(option) => option.label}
            renderInput={(params) => (
              <TextField {...params} placeholder="Select Issue Type" />
            )}
            sx={{ width: 130 }}
          />
        </Grid>
        <Grid item>
          <Autocomplete
            multiple
            limitTags={2}
            disableCloseOnSelect
            options={projects}
            getOptionLabel={(p) => p.name || ""}
            value={selectedProjects}
            onChange={(e, val) => setSelectedProjects(val)}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Projects"
                placeholder={selectedProjects.length === 0 ? "Select Projects" : ""}
              />
            )}
            sx={{
              width: 200,
            }}
          />
        </Grid>
        <Grid item>
          <Autocomplete
            options={payableOptions}
            getOptionLabel={(option) => option.label}
            value={payableOptions.find((opt) => opt.value === payableFilter) || payableOptions[0]}
            onChange={(e, val) => setPayableFilter(val ? val.value : "All")}
            renderInput={(params) => <TextField {...params} label="Payable" />}
            sx={{ width: 100 }}
            disableClearable
          />
        </Grid>
        <Grid item>
          <Button
            disabled={loading}
            type="primary"
            onClick={handleDownload}
            loading={downloadLoading}
            style={{
              backgroundColor: "#008080",
              borderColor: "#008080",
              height: "40px",
            }}
          >
            Download Excel
          </Button>
        </Grid>
      </Grid>
      <AdvancedTable
        columns={columns}
        data={transformedData}
        tableConfig={tableConfig}
        isPagination={false}
        openLoader={loading}
        onCellClick={handleCellClick}
      />
      <ClientTaskDetailsDialog
        open={dialogOpen}
        handleClose={handleDialogClose}
        data={dialogData}
      />
    </div>
  );
};

export default ProjectReportTable;

